"use client"

import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@comps/ui/dialog";
import { TW_AppDialogButton, TW_ButtonList, useHover } from "@utils/twClasses";
import React, { CSSProperties, createContext, useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ConfirmationDialogOptions {
  title: string;
  description: string;
  cancelButtonStyle: CSSProperties;
  cancelButtonHoverStyle: CSSProperties;
  confirmButtonStyle: CSSProperties;
  confirmButtonHoverStyle: CSSProperties;
}

interface ConfirmationDialogProps extends ConfirmationDialogOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

type ConfirmationDialogContextValue = (options: ConfirmationDialogOptions) => Promise<boolean>;

const ConfirmationDialogContext = createContext<ConfirmationDialogContextValue | null>(null);

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  onCancel,
  cancelButtonStyle,
  cancelButtonHoverStyle,
  confirmButtonStyle,
  confirmButtonHoverStyle
}: ConfirmationDialogProps) {
  const { t } = useTranslation();
  const cancelLabel = t("general.button.cancel.text");
  const confirmLabel = t("general.button.confirm.text");
  const cancelHover = useHover(cancelButtonStyle, cancelButtonHoverStyle);
  const confirmHover = useHover(confirmButtonStyle, confirmButtonHoverStyle);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="space-y-2">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription hidden={!description || description === ""} className="whitespace-pre-wrap">{description}</DialogDescription>
        </DialogHeader>
        <div className={TW_ButtonList}>
          <Button key={"cancel-color-" + cancelButtonStyle.color} autoFocus={true} className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>
            {cancelLabel}
          </Button>
          <Button key={"confirm-color-" + confirmButtonStyle.color} className={TW_AppDialogButton} onClick={onConfirm} {...confirmHover}>
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmationDialogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationDialogOptions>({
    title: "",
    description: "",
    cancelButtonStyle: {},
    cancelButtonHoverStyle: {},
    confirmButtonStyle: {},
    confirmButtonHoverStyle: {},
  });
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const activeElementRef = useRef<HTMLElement | null>(null);

  const resolveDialog = useCallback((confirmed: boolean) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setOpen(false);
    const activeElement = activeElementRef.current;
    activeElementRef.current = null;
    if (activeElement) {
      window.setTimeout(() => {
        activeElement.focus({ preventScroll: true });
      }, 0);
    }
    if (resolver) {
      resolver(confirmed);
    }
  }, []);

  const onOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen && resolverRef.current) {
      resolveDialog(false);
      return;
    }
    setOpen(nextOpen);
  }, [resolveDialog]);

  const confirm = useCallback((nextOptions: ConfirmationDialogOptions) => {
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }

    activeElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOptions(nextOptions);
    setOpen(true);

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  return (
    <ConfirmationDialogContext.Provider value={confirm}>
      {children}
      <ConfirmationDialog
        open={open}
        onOpenChange={onOpenChange}
        title={options.title}
        description={options.description}
        onCancel={() => resolveDialog(false)}
        onConfirm={() => resolveDialog(true)}
        cancelButtonStyle={options.cancelButtonStyle}
        cancelButtonHoverStyle={options.cancelButtonHoverStyle}
        confirmButtonStyle={options.confirmButtonStyle}
        confirmButtonHoverStyle={options.confirmButtonHoverStyle}
      />
    </ConfirmationDialogContext.Provider>
  );
}

export function useConfirmationDialog() {
  const context = useContext(ConfirmationDialogContext);
  if (!context) {
    throw new Error("useConfirmationDialog must be used within ConfirmationDialogProvider");
  }
  return context;
}
